/*
  TODO: every save/load call has to go through code-saver/-loader.
  user data must be compressed, and eventually encrypted, too.
*/

import { getCloudProjectList, getCloudUser, serverFeatureAvailable, setCloudProjectList } from "../../features/cloudstorage/cloud";
import { checkUser } from "../../features/cloudstorage/user";
import { changeStatus, deleteDataFromCloud, loadCloudData, removeDataFromCloud, saveData, saveDataToCloud } from "../../features/cloudstorage/wp-json";
import { vfs, createV2CloudZip } from "../../features/filesystem";
import { deleteUserCodeFromLS } from "../../features/localstorage/clean";
import { getLocalStorageProjectList } from "../../features/localstorage/loader";
import { removeProjectFromLocalStorageProjectList, saveUserCodeToLS, saveV2MetaToLS } from "../../features/localstorage/saver";
import { compressAsBase64, compressAsBase64Async } from "../../features/zipping";
import { getAppCfg } from "../app-cfg";
import { EDITOR_VIEW_NAME, SHARE_PROJECT_STATE } from "../constants";
import { cloudProjectId, createCloudId, getProjectMetaFromCloudId } from "../project/projectid";
import { getIconForProject } from "../project/projectslist";
import { ProjectListData } from "../types";
import { getComponentData, getViewData, setComponentData } from "../views";
import { projectListId } from "./loader";

/**
 * Speichert V2-Projekt-Änderungen ins VFS (OPFS - persistent)
 */
async function saveV2ChangesToVFS(editorState: any): Promise<void> {
  // Gleiche Logik wie im projectloader.ts für die projectId
  const projectId = editorState?.project?.id || editorState?.project?.project?.replace(/\s+/g, '_');
  const v2Changes = editorState?.data?.v2EditorChanges || {};
  
  // Keine Änderungen? Nichts zu tun.
  if (Object.keys(v2Changes).length === 0) {
    return;
  }
  
  if (!projectId) {
    console.error('[Saver] V2: Keine projectId gefunden!');
    return;
  }
  
  try {
    const projectRoot = vfs.getProjectRoot(projectId);
    
    // Schreibe jede geänderte Datei ins VFS
    for (const [relativePath, content] of Object.entries(v2Changes)) {
      const fullPath = `${projectRoot}/${relativePath}`;
      await vfs.writeFile(fullPath, content as string);
    }
    
    console.log(`[Saver] V2: ${Object.keys(v2Changes).length} Datei(en) ins VFS gespeichert`);
  } catch (error) {
    console.error('[Saver] V2: Fehler beim Speichern ins VFS:', error);
  }
}

export async function saveUserData() {
  const editorState = getViewData(EDITOR_VIEW_NAME);
  const isV2 = editorState?.project?.version === '2.0';
  
  if (isV2) {
    // V2-Projekt: Dateien ins VFS (OPFS), Meta-Daten ins LocalStorage
    await saveV2ChangesToVFS(editorState);
    saveV2MetaToLS(); // chatMessages, projectProgress, etc.
  } else {
    // Legacy-Projekt: Alles ins LocalStorage
    saveUserCodeToLS();
  }

  // Cloud-Sync (für beide Versionen, falls eingeloggt)
  if (serverFeatureAvailable()) {
    try {
      await checkUser(true);
      console.log("user is logged in!");
      
      if (isV2) {
        // V2: ZIP mit Dateien + Meta
        const v2ZipBase64 = await createV2CloudZip(editorState);
        await saveData(await cloudProjectId(), v2ZipBase64);
        await saveProjectListData();
        console.log("[CloudSync] V2 ZIP saved to cloud");
      } else {
        // Legacy: Komprimiertes JSON
        const data = saveDataToCloud();
        saveUserDataToCloud(await cloudProjectId(), data);
      }
    } catch (error) {
      console.log("user not logged!");
    }
  }
}

export function saveUserDataToCloud(projectId : string, jsonData : string) {
  compressAsBase64(jsonData, async function(compressed : string) {
    const hash = undefined;
    await saveData(projectId, compressed, hash);
    await saveProjectListData();
  });
}

export async function migrateUserDataToCloud(projectId : string, projectData : any) {
  const jsonData = JSON.stringify(projectData);
  const compressed = await compressAsBase64Async(jsonData);    
  const hash = undefined;
  await saveData(projectId, compressed, hash);
}

export async function migrateProjectListEntryToCloud(projectId : string, projectData : any) {  
  console.log('PL Saver: migrating project list entry to cloud', projectId, projectData);
  const projectMetaData = getProjectMetaFromCloudId(projectId);
  const projectDataForList = {
    id: projectMetaData.projectId,
    shareId : await cloudProjectId(projectMetaData.projectId),
    name: projectData.projectName? projectData.projectName : encodeURI("Karte: " + projectMetaData.projectId),
    screenshot: null,
    icon: getIconForProject(projectMetaData.projectId),
    status: "private"
  }
  await saveProjectListData(projectDataForList);  
}

export async function setProjectToPrivate() {
  if (serverFeatureAvailable()) {
    await checkUser(true);
    await changeStatus(await cloudProjectId(), "private");

    const project = getComponentData(SHARE_PROJECT_STATE);
    project.sharingStatus = "private";
    project.url = "";
    setComponentData(SHARE_PROJECT_STATE, project)
    await saveProjectListData();
    // TODO: if login not possible, do something nice!
  }
}

export async function setProjectToPublic(url : string) {
  if (serverFeatureAvailable()) {
    await checkUser(true);
    await changeStatus(await cloudProjectId(), "public");

    const project = getComponentData(SHARE_PROJECT_STATE);
    project.sharingStatus = "public";
    project.url = url;
    setComponentData(SHARE_PROJECT_STATE, project)
    await saveProjectListData();
    // TODO: if login not possible, do something nice!
  }
}


export async function deleteUserData() {
  deleteUserCodeFromLS();
  // TODO check if cloudSaving and compressing enabled!
  if (serverFeatureAvailable()) {
    await checkUser(true);
    const data = removeDataFromCloud();
    const projectId = await cloudProjectId();
    compressAsBase64(data, async function(compressed) {
      console.log("removing data from cloud!");
      const hash = undefined;
      await saveData(projectId, compressed, hash);
    });
     // TODO: if login not possible, do something nice!
  }
}

export async function deleteProject(projectIdToDelete?: string) {
  const currentProjectId = projectIdToDelete || getAppCfg().projectId;
  const currentCloudProjectId = await cloudProjectId(projectIdToDelete);
  
  // If deleting a specific project (not current), we don't need to delete local storage
  if (!projectIdToDelete) {
    // Delete from local storage (only for current project)
    deleteUserCodeFromLS();
  }
  
  // Delete from cloud and remove from project list
  if (serverFeatureAvailable()) {
    try {
      await checkUser(true);
      
      // Get project status from project list if deleting a specific project
      let projectStatus = "private";
      if (projectIdToDelete) {
        const cloudProjectListData = getCloudProjectList();
        const project = cloudProjectListData?.list?.find((element) => element.id === projectIdToDelete);
        projectStatus = project?.status || "private";
      } else {
        // For current project, check share state
        const shareProjectState = getComponentData(SHARE_PROJECT_STATE);
        projectStatus = shareProjectState?.sharingStatus || "private";
      }
      
      // Set project to private first (if it was shared)
      if (projectStatus === "public") {
        await changeStatus(currentCloudProjectId, "private");
      }
      
      // Remove project from project list
      await removeProjectFromList(currentProjectId);
      
      // Delete project data from cloud using DELETE endpoint
      // Try both new and old format to ensure complete deletion
      const user = getCloudUser();
      
      // Delete new format
      const deleteNewResult = await deleteDataFromCloud(currentCloudProjectId);
      console.log('deleteProject: DELETE neues Format:', currentCloudProjectId, 'Result:', deleteNewResult);
      
      // Also delete old format if user has non-hashed username (backward compatibility)
      if (user.userName && typeof user.userName === 'string' && !user.userName.startsWith('UID_')) {
        const oldCloudProjectId = await createCloudId(user.userName, currentProjectId, false); // false = don't hash
        const deleteOldResult = await deleteDataFromCloud(oldCloudProjectId);
        console.log('deleteProject: DELETE altes Format:', oldCloudProjectId, 'Result:', deleteOldResult);
      }
    } catch (error) {
      console.log("deleteProject: Error:", error);
      // Even if cloud deletion fails, remove from local list
      await removeProjectFromList(currentProjectId);
    }
  } else {
    // If no cloud storage, just remove from local list
    await removeProjectFromList(currentProjectId);
  }

}

async function removeProjectFromList(projectId: string) {
  // STEP 1: Load list from cloud FIRST
  const response = await loadCloudData(projectListId(), false);
  if (!response.success || !response.data) {
    console.error('removeProjectFromList: ERROR - failed to load list from cloud!', response);
    return;
  }
  
  // Get the list from cloud response
  const cloudList = response.data.list || response.data;
  if (!Array.isArray(cloudList)) {
    console.error('removeProjectFromList: ERROR - cloud list is not an array!', cloudList);
    return;
  }
  
  const cloudCountBefore = cloudList.length;
  const cloudIdsBefore = cloudList.map((p: any) => p.id);
  
  const localStorageListBefore = getLocalStorageProjectList();
  const localStorageCountBefore = localStorageListBefore.length || 0;
  const localStorageIdsBefore = localStorageListBefore.map((p: any) => p.id);
  
  // STEP 2: Remove project from list
  const newList = cloudList.filter((element: any) => element.id !== projectId);
  const cloudCountAfter = newList.length;
  const cloudIdsAfter = newList.map((p: any) => p.id);
  
  if (newList.length === cloudList.length) {
    console.log('removeProjectFromList: WARNING - project not found!', projectId);
    return;
  }

  
  // STEP 3: Update local state
  const newProjectList = { list: newList };
  setCloudProjectList(newProjectList);
  
  // STEP 4: Save new list to cloud
  if (serverFeatureAvailable()) {

    await saveData(projectListId(), newProjectList);
    
    
    // TEST: Direkt nach dem Speichern die Liste neu laden und prüfen
    const verifyResponse = await loadCloudData(projectListId(), false);
    if (verifyResponse.success && verifyResponse.data) {
      const verifiedList = verifyResponse.data.list || verifyResponse.data;
      const verifiedIds = Array.isArray(verifiedList) ? verifiedList.map((p: any) => p.id) : [];
      const stillInList = verifiedIds.includes(projectId);
      
      console.log('removeProjectFromList: VERIFIKATION NACH SPEICHERN');
      console.log('removeProjectFromList: Geladene Liste - Projekte:', verifiedList?.length || 0, '| IDs:', verifiedIds);
      console.log('removeProjectFromList: Projekt noch in Liste?', stillInList ? 'JA - FEHLER!' : 'NEIN - OK');

    } else {
      console.error('removeProjectFromList: FEHLER - Konnte Liste nicht zur Verifikation laden!', verifyResponse);
    }
  }
  
  // STEP 5: Remove from LocalStorage project list
  removeProjectFromLocalStorageProjectList(projectId);
}

async function saveProjectListData(projectData? : any) {
  const cloudProjectListData = getCloudProjectList();  
  let projectListData = cloudProjectListData;
  if (projectListData === null) {
    projectData = {}
  }

  let projectDataToSave = projectData;
  if (projectData === undefined) {
    const currentProjectId = getAppCfg().projectId;
    projectDataToSave = {
      id: currentProjectId,
      shareId : await cloudProjectId(currentProjectId),
      name: encodeURI(getViewData(EDITOR_VIEW_NAME).project.project),
      screenshot: null,
      icon: getIconForProject(currentProjectId),
      status: getComponentData(SHARE_PROJECT_STATE).sharingStatus
    }
  }

  let listElementIndex = -1;
  let newList =[];
  if (projectListData.list !== undefined) {
    listElementIndex = projectListData.list.findIndex((element) => element.id === projectDataToSave.id);
    newList = [ ...projectListData.list ];
  }
  if (listElementIndex > -1) {
    const newEntry = {
      ...newList[listElementIndex],
      name: projectDataToSave.name,
      screenshot: projectDataToSave.screenshot,
      icon: projectDataToSave.icon,
      status: projectDataToSave.status
    }
    // Update shareId if provided in projectDataToSave
    if (projectDataToSave.shareId) {
      newEntry.shareId = projectDataToSave.shareId;
    }
    newList[listElementIndex] = newEntry;
  } else {
    newList.push(projectDataToSave);
  }
  const newProjectList = { list: newList }
  setCloudProjectList(newProjectList);
  await saveData(projectListId(), newProjectList);
}

export function updateProjectListData(projectListData : ProjectListData) {  
  saveData(projectListId(), { list: projectListData.list });
}
